import { GenericAbortSignal } from 'axios';
import { format } from 'date-fns';

import { NONE, PHYSICIAN_TEAM } from 'constants/filterKeysConstants';
import FilterValueArray from 'models/filterValues/FilterValueArray';
import FilterValueBoolean from 'models/filterValues/FilterValueBoolean';
import LocationEpisode, { LocationEpisodeOptions } from 'models/LocationEpisode';
import { Paginated } from 'models/Paginated';
import http from 'services/http';
import { DischargeCalendarFilterState } from 'stores/dischargeCalendarStore';

import { BaseIndexQueryParams } from './types';

type DischargeCalendarParams = {
  startDate: Date;
  endDate: Date;
  filters?: DischargeCalendarFilterState;
  search?: string;
} & BaseIndexQueryParams;

function serializeFilters(filters: DischargeCalendarFilterState) {
  return Object.entries(filters).reduce(
    (acc, [key, value]) => {
      if (value instanceof FilterValueArray) {
        if (!value || value.length === 0) return acc;

        if (key === PHYSICIAN_TEAM) {
          acc[key] = value.map((x) => (x.name === NONE ? x.id : x.name));
        } else {
          acc[key] = value.map((x) => x.id).join(',');
        }
      } else if (value instanceof FilterValueBoolean) {
        if (value.value) {
          acc[key] = value.value;
        }
      }
      return acc;
    },
    {} as Record<string, any>
  );
}

export async function indexDischargeCalendar(
  { startDate, endDate, filters, search, ...params }: DischargeCalendarParams,
  signal: GenericAbortSignal
) {
  const serializedParams = {
    ...params,
    ...serializeFilters(filters ?? {}),
    search,
    projectedDischargeDateRange: [
      format(startDate, 'yyyy-MM-dd') || new Date('1900-01-01'),
      format(endDate, 'yyyy-MM-dd') || new Date('2999-01-01'),
    ],
    include: 'episode,plan_type_classification,reviews.group',
    active: true,
  };

  return http
    .get<Paginated<LocationEpisodeOptions>>('/location_episodes/discharge_calendar', {
      params: serializedParams,
      signal,
    })
    .then((res) => ({
      data: res.data.data.map((le) => new LocationEpisode(le)),
      meta: res.data.meta,
    }));
}

export const dischargeCalendarQueryKeys = {
  base: 'dischargeCalendar',
  index: (params: Record<string, any> = {}) => ['dischargeCalendar', 'index', params],
};
